import { useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import _ from 'lodash';
import cx from "classnames";

interface renderItem  {
    component: React.ReactNode;
    title?: string;
};

interface VerticalScrollSliderProps {
    listOfRenders: renderItem[];
    selectedIndex: number;
    classnames?: string;
    gap?: number;
    offsetTopBottom?: number;
    onChange?: (index: number) => void;
    enablePrevAndNext?: boolean;
    enableWheelScroll?: boolean;
}

export const VerticalScrollSlider = ({
    listOfRenders,
    selectedIndex = 0,
    classnames,
    gap = 0,
    offsetTopBottom = 0,
    onChange,
    enablePrevAndNext = false,
    enableWheelScroll = true,
}: VerticalScrollSliderProps) => {

    const container = useRef<HTMLDivElement | null>(null); // parent container
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]); // item refs

    const previousIndex = useRef<number | null>(null); // previous index
    const throttleScrollY = useRef(false); // throttle scroll event
    
    const scrollTo = (index: number) => { // scroll to item
        const el = itemRefs.current[index]; // element to scroll to
        const parent = container.current; // scrolling the parent container to make the selected item in view
        if (el && parent) { // check if element and parent exist
          parent.scrollTo({
            top: el.offsetTop,
            behavior: "smooth",
          });
        }
    }

    const attachScrollListener = () => { // Listen for the scroll
        if(!enableWheelScroll) return; // if wheel scroll is disabled, do not attach listener

        const el = container.current; 
        if (!el) return;

        const handleScrollEnd =(deltaY) => { // fires immendiately
            const direction = deltaY > 0 ? "down" : "up";

            if (direction === "down" && selectedIndex < listOfRenders.length - 1) {
                onChange?.(selectedIndex + 1);
            } else if (direction === "up" && selectedIndex > 0) {
                 onChange?.(selectedIndex - 1);
            }

            setTimeout(() => { // reset throttle after 1s of inactivity
             throttleScrollY.current = false;
            }, 1000);
        };

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault(); // block normal scroll
            if(!throttleScrollY.current){
             throttleScrollY.current = true;
              handleScrollEnd(e.deltaY);
            }
        };

        el.addEventListener('wheel', handleWheel, { passive: false });

       return () => {
            el.removeEventListener("wheel", handleWheel);
        };
    }

    useEffect(() => {

        if (previousIndex.current === null || previousIndex.current !== selectedIndex) { // scroll to default index on mount || compare previous and current index if diff change scroll to index
          scrollTo(selectedIndex); // scroll to
        } 

        previousIndex.current = selectedIndex; // update previous index to current 
        const detachScrollListener = attachScrollListener(); // scroll listener
        return detachScrollListener; // cleanup on unmount
    }, [selectedIndex]);

    // Methods
    const changePrevItem = () => {
        if(selectedIndex === 0) return;
        onChange?.(selectedIndex - 1);
    }

    const changeNextItem = () => {
        if(selectedIndex === _.size(listOfRenders) - 1) return;
        onChange?.(selectedIndex + 1);
    }

    return (
    <div className={cx(styles.container, classnames)}>
         {/* prev */}
         {enablePrevAndNext && selectedIndex !== 0 && <div 
            className={cx(styles.prevAndNext, styles.top)} 
            onClick={() => changePrevItem()}>
          <span>previous:</span>
          <p className={styles.fadeIn} key={`${selectedIndex - 1}prev`}>{listOfRenders[selectedIndex - 1]?.title}</p>
          <i className='paperflite-arrow-up'/>    
        </div>}
        <div tabIndex={-1} className={styles.renderContainer} ref={container} style={{gap: `${gap}rem`, padding: `${offsetTopBottom}rem 0`}}>
             { _.map(listOfRenders, (item, index) => { 
                 return <div className={styles.renderItem} ref={(el) => itemRefs.current[index] = el }>
                         {item.component}
                        </div>
            })} 
        </div>
          {/* next */}
           {enablePrevAndNext && selectedIndex !== _.size(listOfRenders) - 1 && <div 
              className={cx(styles.prevAndNext, styles.bottom)} 
              onClick={() => changeNextItem()}>
                <span>next:</span>   
                <p className={styles.fadeIn} key={`${selectedIndex + 1}next`}> {listOfRenders[selectedIndex + 1]?.title} </p>
                <i className='paperflite-down-arrow'/> 
            </div>}
    </div>
    )
}
